import { $createTextNode, $getSelection, $isParagraphNode, $isRangeSelection, $isTextNode, ElementNode, LexicalEditor, ParagraphNode } from "lexical";

import { $isLinkNode, TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $createListItemNode, $createListNode, ListItemNode } from "@lexical/list";
import { $createHeadingNode } from "@lexical/rich-text";

import { $createImageNode } from "components/ContentEditor/ImageNode";

// Helper to find existing link in node or its parents
const findExistingLink = (node: ElementNode | null): string | null => {
	if (!node) return null;
	if ($isLinkNode(node)) return node.getURL();

	let parent = node.getParent();
	while (parent) {
		if ($isLinkNode(parent)) return parent.getURL();
		parent = parent.getParent();
	}
	return null;
};

// Helper to convert children from one node to another
const transferChildren = (fromNode: ElementNode, toNode: ElementNode) => {
	const children = fromNode.getChildren();
	if (children?.length) {
		children.forEach((child) => toNode.append(child));
	} else {
		toNode.append($createTextNode());
	}
};

// Helper to convert block nodes (without selection - selection handled by caller)
const convertBlockNode = (nodeToConvert: ElementNode, newNode: ElementNode): ElementNode => {
	transferChildren(nodeToConvert, newNode);
	return nodeToConvert.replace(newNode);
};

export const handleLink = (editor: LexicalEditor) => {
	editor.update(() => {
		const selection = $getSelection();
		if (!$isRangeSelection(selection)) return;

		const existingUrl = selection.getNodes().find((node) => {
			const parent = node.getParent() as ElementNode | null;
			return findExistingLink(parent) !== null;
		});
		const url = existingUrl ? findExistingLink(existingUrl.getParent() as ElementNode | null) : null;
		const message = "Legg inn URL. Nettlenke må starte med https://";
		const newUrl = prompt(message, url || "");

		if (newUrl !== null) {
			editor.dispatchCommand(TOGGLE_LINK_COMMAND, newUrl ? { url: newUrl } : null);
		}
	});
};

export const handleHeading = (editor: LexicalEditor, headingLevel: "h2" | "h3") => {
	editor.update(() => {
		const selection = $getSelection();
		if (!$isRangeSelection(selection)) return;

		const uniqueBlocks = new Set<ElementNode>();
		selection.getNodes().forEach((node) => {
			const parent = node.getParent() as ElementNode | null;
			if (parent && ["paragraph", "heading"].includes(parent.getType())) {
				uniqueBlocks.add(parent);
			}
		});

		const blockArray = Array.from(uniqueBlocks);
		const convertedBlocks: ElementNode[] = [];
		blockArray.forEach((blockNode) => {
			const newHeading = $createHeadingNode(headingLevel);
			const newNode = convertBlockNode(blockNode, newHeading);
			convertedBlocks.push(newNode);
		});

		// Select the last converted block after all conversions are complete
		const lastBlock = convertedBlocks[convertedBlocks.length - 1];
		if (lastBlock) {
			lastBlock.selectStart();
		}
	});
};

export const handleParagraph = (editor: LexicalEditor) => {
	editor.update(() => {
		const selection = $getSelection();
		if (!$isRangeSelection(selection)) return;

		const uniqueBlocks = new Set<ElementNode>();
		selection.getNodes().forEach((node) => {
			const parent = node.getParent() as ElementNode | null;
			if (parent && ["paragraph", "heading"].includes(parent.getType())) {
				uniqueBlocks.add(parent);
			}
		});

		const blockArray = Array.from(uniqueBlocks);
		const convertedBlocks: ElementNode[] = [];
		blockArray.forEach((blockNode) => {
			const newNode = convertBlockNode(blockNode, new ParagraphNode());
			convertedBlocks.push(newNode);
		});

		// Select the last converted block after all conversions are complete
		const lastBlock = convertedBlocks[convertedBlocks.length - 1];
		if (lastBlock) {
			lastBlock.selectStart();
		}
	});
};

export const handleList = (editor: LexicalEditor) => {
	editor.update(() => {
		const selection = $getSelection();
		if (!$isRangeSelection(selection)) return;

		const uniqueBlocks = new Set<ElementNode>();
		selection.getNodes().forEach((node) => {
			const blockNode = ($isParagraphNode(node) ? node : node.getParent()) as ElementNode | null;
			if (blockNode && ["paragraph", "heading"].includes(blockNode.getType())) {
				uniqueBlocks.add(blockNode);
			}
		});

		let lastListItem: ListItemNode | null = null;
		uniqueBlocks.forEach((blockNode) => {
			const listNode = $createListNode("bullet");
			const listItem = $createListItemNode();
			transferChildren(blockNode, listItem);
			listNode.append(listItem);
			blockNode.replace(listNode);
			lastListItem = listItem;
		});

		// Select the last list item after all conversions are complete
		if (lastListItem) {
			(lastListItem as ListItemNode).selectStart();
		}
	});
};

export const handleImage = (editor: LexicalEditor) => {
	const imageUrl = prompt("Enter image URL:");
	if (!imageUrl) return;

	const altText = prompt("Enter alt text (optional):", "") || "";

	editor.update(() => {
		const selection = $getSelection();
		if ($isRangeSelection(selection)) {
			const imageNode = $createImageNode({
				src: imageUrl,
				altText,
				width: "auto",
				height: "auto",
			});
			selection.insertNodes([imageNode]);
		}
	});
};

export const handleClearFormatting = (editor: LexicalEditor) => {
	editor.update(() => {
		const selection = $getSelection();
		if (!$isRangeSelection(selection)) return;

		selection.getNodes().forEach((node) => {
			if ($isTextNode(node)) {
				node.setFormat(0);
			} else if ($isLinkNode(node)) {
				const children = node.getChildren();
				children.forEach((child) => node.insertBefore(child));
				node.remove();
			}
		});
	});
};
